import { getDateRange } from "@/components/calendars/dateRange.ts";
import type { ViewType } from "@/components/calendars/types.ts";
import { useMediaQuery } from "@/hooks/useMediaQuery.tsx";
import type { CalendarView } from "@/types/messageViewContext.ts";
import { parseISO } from "date-fns";
import { getDefaultStore, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from "react";

// ─── Durable base-calendar store ────────────────────────────────────────────
// The atoms + their storage backing, colocated with their sole consumer (the
// hook below). The two atoms deliberately persist differently: the VIEW is a
// lasting preference (plain localStorage — leaving the calendar on multi-day
// means reopening it on multi-day), while the DATE is TTL'd sessionStorage so
// a return after an idle hour opens on today instead of a stale anchor. Only
// the storage helper + reset are exported (for tests); the atoms are
// module-private and read through the hook.

/**
 * Idle max-age for the shared base-calendar date. The window slides from the
 * last write, so an in-tab reload within the hour rehydrates the stored date
 * while a truly idle hour lapses the calendar back to today. The divider width
 * is deliberately NOT here — it persists indefinitely in localStorage (see
 * {@link useResizablePanel}).
 */
export const CANVAS_STORE_TTL_MS = 60 * 60 * 1000;

/**
 * localStorage key backing the view atom. Account-scoped: `clearClientState`
 * removes it on account switch, and logout wipes it with everything else.
 */
export const CALENDAR_VIEW_STORAGE_KEY = "okthx_canvas_calendar_view";

/** sessionStorage keys backing the TTL'd base-calendar atoms. */
const CANVAS_STORE_KEYS = {
  calendarDate: "okthx_canvas_calendar_date",
} as const;

/** Stored shape: the value plus the wall-clock time it stops being valid. */
type Envelope<T> = { value: T; expiresAt: number };

/** An envelope is any object carrying a numeric `expiresAt`; a bare optional
 *  chain suffices since primitives and `null` yield `undefined`. */
function isEnvelope<T>(raw: unknown): raw is Envelope<T> {
  return typeof (raw as Envelope<T> | undefined)?.expiresAt === "number";
}

/**
 * A Jotai `SyncStorage` over `sessionStorage` that expires each key after an idle
 * max-age. Values are wrapped in a `{ value, expiresAt }` envelope; every write
 * bumps `expiresAt` to `now() + ttlMs` (a sliding window from the last write),
 * and a read past `expiresAt` returns the caller's `initialValue` — so state
 * survives an in-tab reload within the window but resets to default once the
 * window lapses. The clock is injectable so the TTL logic is unit-testable
 * without mocking `Date.now`. Hand-rolled because Jotai ships no TTL-aware
 * storage adapter.
 */
export function createTtlSessionStorage<T>({
  now = Date.now,
  ttlMs = CANVAS_STORE_TTL_MS,
}: { now?: () => number; ttlMs?: number } = {}): SyncStorage<T> {
  return {
    getItem: (key, initialValue) => {
      let stored: string | null;
      try {
        stored = sessionStorage.getItem(key);
      } catch {
        return initialValue;
      }
      if (stored === null) return initialValue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(stored);
      } catch {
        return initialValue;
      }
      if (!isEnvelope<T>(parsed) || now() > parsed.expiresAt) return initialValue;
      return parsed.value;
    },
    setItem: (key, value) => {
      const envelope: Envelope<T> = { value, expiresAt: now() + ttlMs };
      try {
        sessionStorage.setItem(key, JSON.stringify(envelope));
      } catch {
        // Private-mode / quota failures: skip persistence, keep the in-memory atom.
      }
    },
    removeItem: (key) => {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Ignore — nothing to clean up if storage is unavailable.
      }
    },
  };
}

/**
 * A Jotai `SyncStorage` over plain localStorage — no TTL envelope, the value
 * persists until removed. Same failure posture as the TTL adapter: private-mode
 * or quota errors degrade to the in-memory atom instead of throwing.
 */
function createLocalStorage<T>(): SyncStorage<T> {
  return {
    getItem: (key, initialValue) => {
      try {
        const stored = localStorage.getItem(key);
        return stored === null ? initialValue : (JSON.parse(stored) as T);
      } catch {
        return initialValue;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Private-mode / quota failures: skip persistence, keep the in-memory atom.
      }
    },
    removeItem: (key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore — nothing to clean up if storage is unavailable.
      }
    },
  };
}

const calendarViewStorage = createLocalStorage<ViewType | null>();

/**
 * Base calendar view mode, shared across surfaces. `null` → the consumer's
 * breakpoint default (the serialized store can't read a media query, so the
 * default stays out of it). Durable: a chosen view survives reloads and app
 * closes until logout/account switch clears it.
 */
const calendarViewAtom = atomWithStorage<ViewType | null>(
  CALENDAR_VIEW_STORAGE_KEY,
  null,
  calendarViewStorage,
  { getOnInit: true },
);

/**
 * Base calendar anchor date as an ISO string, shared across surfaces. `null` →
 * today; on TTL lapse it returns to `null`, so the calendar resets to today.
 */
const calendarDateAtom = atomWithStorage<string | null>(
  CANVAS_STORE_KEYS.calendarDate,
  null,
  createTtlSessionStorage<string | null>(),
  { getOnInit: true },
);

/** Test-only: clear the in-memory and persisted shared canvas state between cases. */
export function __resetSharedCanvasStore(): void {
  const store = getDefaultStore();
  store.set(calendarViewAtom, null);
  store.set(calendarDateAtom, null);
  calendarViewStorage.removeItem(CALENDAR_VIEW_STORAGE_KEY);
  for (const key of Object.values(CANVAS_STORE_KEYS)) {
    sessionStorage.removeItem(key);
  }
}

// ─── Default resolution (shared) ─────────────────────────────────────────────
// The `lg` breakpoint default view and today fallback are single-sourced so the
// reactive hook and imperative reader apply the same resolution rules.

/**
 * The `lg` breakpoint (1024px) media query — the day/week fallback boundary.
 * Single-sourced for the store-level listener and {@link readCalendarWindow};
 * {@link useSharedCalendarState} reads the same boundary via `useMediaQuery("lg")`.
 */
const LG_MEDIA_QUERY = "(min-width: 1024px)";

/** The breakpoint default view: `week` at ≥lg, else `day`. The serialized store
 *  can't read a media query, so a `null` stored view resolves to this. */
const defaultViewForBreakpoint = (matchesLg: boolean): ViewType => (matchesLg ? "week" : "day");

/**
 * Resolve a stored (nullable) view against the breakpoint. Below `lg` a stored
 * `week` falls back to the default: mobile's options sheet offers day,
 * multi-day, and month but not week, and since the view persists indefinitely,
 * a desktop-only choice could otherwise reach a phone (a shared `?view=week`
 * link, a resize while the tab was closed) and stay stranded — the old TTL
 * used to age such values out.
 */
function resolveViewForBreakpoint(storedView: ViewType | null, matchesLg: boolean): ViewType {
  if (storedView === null || (!matchesLg && storedView === "week")) {
    return defaultViewForBreakpoint(matchesLg);
  }
  return storedView;
}

/**
 * The views a desktop surface may offer at this width — the picker's menu, the
 * calendars dialog's rows.
 *
 * Week only from `lg`, because that is where {@link resolveViewForBreakpoint}
 * will keep it: below 1024px a stored `week` is coerced back to `day`, so a Week
 * control on a narrower window changes nothing and springs back. `multi-day` is
 * absent throughout — it is the phone layout, offered by the mobile sheet.
 *
 * Beside the coercion rather than in either surface, because there are two of
 * them: the dialog learned this rule first and the header's picker went on
 * offering Week for another review cycle.
 */
export const desktopViewsForBreakpoint = (matchesLg: boolean): ViewType[] =>
  matchesLg ? ["day", "week", "month"] : ["day", "month"];

/** Resolve the stored (nullable) anchor date to a concrete `Date` — today when
 *  absent or TTL-lapsed. */
const resolveStoredDate = (storedDate: string | null): Date =>
  storedDate ? parseISO(storedDate) : new Date();

// ─── Window resolution and imperative access ─────────────────────────────────

/**
 * Resolve the (nullable) stored view/date into the base-calendar window for the
 * per-message `viewContext` snapshot: the view mode plus the ISO range it covers.
 * The shared source of truth for the view/date defaults used by the reactive
 * hook and the imperative {@link readCalendarWindow} reader.
 *
 * `multi-day` follows the renderer's breakpoint mapping: the compact thread
 * canvas shows two days, while the wide canvas falls back to a single day.
 */
export function resolveCalendarWindow(
  storedView: ViewType | null,
  storedDate: string | null,
  matchesLg: boolean,
): CalendarView {
  const resolvedView = resolveViewForBreakpoint(storedView, matchesLg);
  const isCompactMultiDay = resolvedView === "multi-day" && !matchesLg;
  const viewType: CalendarView["viewType"] =
    resolvedView === "multi-day" && matchesLg ? "day" : resolvedView;
  const { start, end } = getDateRange(
    resolveStoredDate(storedDate),
    viewType,
    isCompactMultiDay ? 2 : 1,
  );
  return { viewType, startISO: start.toISOString(), endISO: end.toISOString() };
}

/**
 * Imperative, hook-free read of {@link resolveCalendarWindow} from the default
 * store (the app runs provider-less, and both atoms use `getOnInit`, so the read
 * is hydrated even pre-mount). Safe to call outside React at message-send time —
 * it takes no subscription, so it never churns a caller's identity.
 */
export function readCalendarWindow(): CalendarView {
  const store = getDefaultStore();
  const matchesLg =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(LG_MEDIA_QUERY).matches;
  return resolveCalendarWindow(store.get(calendarViewAtom), store.get(calendarDateAtom), matchesLg);
}

/**
 * Imperative, hook-free move of the base calendar — the calendar focus
 * boundary's write when a `/calendar?date=…&view=…` link is claimed. Taking no
 * subscription matters here: the boundary wraps a whole page, so re-rendering it
 * on every date change would re-render that page's entire subtree.
 *
 * The view is set exactly as asked (no snap-to-today), since a focus link
 * supplies its date and view together.
 */
export function moveSharedCalendar({ date, view }: { date?: Date; view?: ViewType }): void {
  const store = getDefaultStore();
  if (date) store.set(calendarDateAtom, date.toISOString());
  if (view) store.set(calendarViewAtom, view);
}

// No breakpoint listener writes the view any more, and that absence is the
// point. A crossing used to store the viewport's default — harmless while the
// view lived in TTL'd sessionStorage, since the overwrite aged out within the
// hour. Durable storage turns the same write into permanent damage: resizing
// past 1024px twice would replace a chosen Month with Week for good.
//
// Nothing is lost by dropping it, because the breakpoint has never been the
// store's business. Every read resolves through `resolveViewForBreakpoint`, so
// a phone holding a stored `week` already displays `day` — and the stored
// choice comes back when the window does.

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * `useState`-shaped view over the durable base-calendar atoms so a calendar
 * surface (home, thread canvas) keeps the same view/date across navigation.
 * Presents a `Date` + `ViewType` API while the store holds an ISO string + a
 * nullable view; a `null` view (never chosen) reads back as the breakpoint
 * default, and a `null` date (absent or TTL-lapsed) as today, so a lapse
 * resets the calendar without special-casing at the call site.
 *
 * The breakpoint default (`week` on `lg`, else `day`) is resolved here — the one
 * source of truth — rather than passed in, so every call site stays in sync
 * (continuity only holds while the fallback is identical everywhere).
 */
export const useSharedCalendarState = (): {
  currentDate: Date;
  setCurrentDate: Dispatch<SetStateAction<Date>>;
  viewType: ViewType;
  setViewType: Dispatch<SetStateAction<ViewType>>;
} => {
  // `lg` = 64rem (1024px) — the day/week breakpoint. Reading it here keeps the
  // fallback view identical across home, the thread canvas, and the header.
  const matchesLg = useMediaQuery("lg");
  const [storedDate, setStoredDate] = useAtom(calendarDateAtom);
  const [storedView, setStoredView] = useAtom(calendarViewAtom);

  const currentDate = useMemo(() => resolveStoredDate(storedDate), [storedDate]);

  useEffect(() => {
    if (storedDate !== null) return;
    setStoredDate((previous) => previous ?? currentDate.toISOString());
  }, [currentDate, setStoredDate, storedDate]);

  const setCurrentDate = useCallback<Dispatch<SetStateAction<Date>>>(
    (update) =>
      setStoredDate((prev) => {
        const prevDate = resolveStoredDate(prev);
        const next = typeof update === "function" ? update(prevDate) : update;
        return next.toISOString();
      }),
    [setStoredDate],
  );

  const viewType = resolveViewForBreakpoint(storedView, matchesLg);

  const setViewType = useCallback<Dispatch<SetStateAction<ViewType>>>(
    (update) =>
      setStoredView((prev) =>
        typeof update === "function" ? update(resolveViewForBreakpoint(prev, matchesLg)) : update,
      ),
    [setStoredView, matchesLg],
  );

  return { currentDate, setCurrentDate, viewType, setViewType };
};
